<template>
  <el-dialog
    v-model="dialogVisible"
    title="导入客户"
    width="500px"
    @close="handleClose"
  >
    <!-- 模板下载区域 -->
    <div class="import-template">
      <el-button 
        type="text" 
        @click="handleDownloadTemplate"
      >
        下载客户导入模板
      </el-button>
      <p class="template-tip">提示：请按照模板格式填写客户信息</p>
    </div>

    <!-- 文件上传区域：核心优化单文件替换与重传 -->
    <div class="upload-area">
      <el-upload
        class="upload-demo"
        :action="uploadApi"
        drag
        :headers="uploadHeaders"
        :file-list="fileList"
        :before-upload="beforeUpload"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :on-remove="handleFileRemove"
        :on-exceed="handleFileExceed"
        accept=".xlsx,.xls"
        :auto-upload="true"
        :limit="1"
        :disabled="isUploading" 
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">
          将文件拖到此处，或 <em>点击上传</em>
        </div>
        <template #tip>
          <div class="el-upload__tip"> 仅支持 .xlsx / .xls 格式文件</div>
        </template>
      </el-upload>
    </div>

    <template #footer>
      <el-button @click="handleClose">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script lang="js">
import { defineComponent, reactive, ref, toRefs, watch } from 'vue'
import { ElMessage, ElIcon } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'  // 引入加载图标
import { UPLOAD_BASE_URL } from '@/common/constant'
import { getUploadCustomerTemplate } from '@/service/main/goods'
import localCache from '@/utils/cache'

export default defineComponent({
  components: { ElIcon, Loading },  // 注册加载图标组件
  props: {
    // 弹窗显示状态（父子组件双向绑定）
    visible: {
      type: Boolean,
      default: false
    },
  },
  emits: [
    'update:visible',  // 关闭弹窗事件
    'import-success'   // 导入成功事件（返回新客户列表）
  ],
  setup(props, { emit }) {
    // 上传相关配置
    const uploadApi = UPLOAD_BASE_URL + '/customer/web/uploadCustomer'
    const uploadHeaders = reactive({
      'User-Id': localCache.getCache('adminInfo')?.userId || ''  // 增加空值保护
    })

    // 状态管理：新增上传中状态、强化文件列表控制
    const state = reactive({
      dialogVisible: props.visible,
      fileList: [],          // 仅存1个文件的列表
      templateUrl: '',       // 模板下载链接
      isUploading: false     // 新增：上传中状态，防止重复提交
    })

    // 1. 监听弹窗显示状态：关闭时重置所有状态
    watch(() => props.visible, (val) => {
      state.dialogVisible = val
      if (!val) {  // 弹窗关闭时
        state.fileList = []
        state.isUploading = false
      }
    }, { immediate: true })  // 初始触发一次，同步弹窗状态

    // 2. 下载导入模板（保持原有逻辑，增加空值保护）
    const handleDownloadTemplate = async () => {
      try {
        const res = await getUploadCustomerTemplate()
        if (!res?.data) throw new Error('模板链接获取失败')
        
        state.templateUrl = res.data
        const link = document.createElement('a')
        link.href = state.templateUrl
        link.download = '客户导入模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        ElMessage.error(error.message || '模板下载失败，请重试')
        console.error('下载模板错误:', error)
      }
    }

    // 3. 上传前校验：清除旧文件 + 基础校验
    const beforeUpload = (file) => {
      // 核心：替换旧文件（清空列表，确保仅保留当前新文件）
      if (state.fileList.length > 0) {
        state.fileList = []  // 移除旧文件
        ElMessage.info('已替换为新文件，正在重新上传...')
      }

      // 校验文件格式（双重校验：MIME类型 + 后缀名，兼容部分异常文件）
      const fileExt = file.name.split('.').pop().toLowerCase()
      const isExcelExt = ['xlsx', 'xls'].includes(fileExt)
      const isExcelMime = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        || file.type === 'application/vnd.ms-excel'
      
      if (!isExcelExt || !isExcelMime) {
        ElMessage.error('请上传 .xlsx 或 .xls 格式的Excel文件')
        return false
      }

      // 校验文件大小（5MB以内）
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        ElMessage.error('文件大小不能超过 5MB，请压缩文件后重试')
        return false
      }

      // 重置上传状态：清除上次结果，标记为上传中
      state.isUploading = true
      return true
    }

    // 4. 处理文件超出限制（当已存在文件时，选择新文件触发）
    const handleFileExceed = (files) => {
      // 仅取第一个新文件（避免多文件选择）
      const newFile = files[0]
      // 移除旧文件，添加新文件
      state.fileList = [newFile]
      // 手动触发新文件上传（模拟ElUpload自动上传逻辑）
      const uploadInput = document.querySelector('.upload-demo input[type="file"]')
      if (uploadInput) {
        // 清空input值后重新赋值，触发上传
        uploadInput.value = ''
        // 创建临时FileList替换input的files（兼容浏览器限制）
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(newFile)
        uploadInput.files = dataTransfer.files
        // 触发input的change事件，启动上传
        uploadInput.dispatchEvent(new Event('change'))
      }
    }

    // 5. 移除文件时的处理：重置状态
    const handleFileRemove = () => {
      state.fileList = []
      state.isUploading = false
      // ElMessage.info('已移除上传文件')
    }

    // 6. 上传成功处理：同步新客户数据到父组件
    const handleUploadSuccess = (response) => {
      state.isUploading = false  // 结束上传中状态
      if (response.code === 0) {
        const { itemList = [] } = response.data
        // 通知父组件：导入成功，传递新客户列表
        emit('import-success', itemList)
        handleClose()
        // ElMessage.success(`文件上传成功，新增 ${itemList.length} 位客户`)
      } else {
        ElMessage.error(response.msg || '文件上传失败，请检查文件格式后重试')
      }
    }

    // 7. 上传失败处理：重置状态 + 错误提示
    const handleUploadError = (error) => {
      state.isUploading = false  // 结束上传中状态
      ElMessage.error('上传失败，请检查网络连接或联系管理员')
      console.error('上传错误详情:', error)
    }

    // 8. 关闭弹窗：重置所有状态
    const handleClose = () => {
      state.fileList = []
      state.isUploading = false
      emit('update:visible', false)  // 通知父组件关闭弹窗
    }

    return {
      ...toRefs(state),
      uploadApi,
      uploadHeaders,
      handleDownloadTemplate,
      beforeUpload,
      handleUploadSuccess,
      handleUploadError,
      handleClose,
      handleFileExceed,
      handleFileRemove
    }
  }
})
</script>

<style scoped lang="less">
.import-template {
  text-align: left !important;
  margin-bottom: 20px;
  .template-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #666;
  }
}

.upload-area {
  text-align: left !important;
  margin-bottom: 20px;
  width: 100%;
  .upload-demo{
    width: 100%;
  }
}

.upload-result {
  .result-content {
    font-size: 13px;
    line-height: 24px;
    .result-label {
      display: inline-block;
      width: 80px;
      color: #666;
    }
    .success {
      color: #13ce66;
    }
    .error {
      color: #ff4949;
    }
    .fail-reason {
      margin-top: 10px;
      p {
        margin-bottom: 8px;
        color: #666;
      }
      &:deep(.el-table) {
        font-size: 12px;
      }
    }
  }
}
:deep(.el-upload){
  text-align: left !important;
}
:deep(.el-upload-dragger){
  width: 460px !important;
}
</style>